import {
    DELIVERY_SPEED,
    DELIVERY_SPEED_DISPLAY,
    DELIVERY_SPEED_STRATEGY,
    DeliverySpeedDetail,
    DeliverySpeedOption,
} from "@swiggy-private/cart-types";
import { MINS } from "@cart-ui/constants/DeliverySpeed";
import { TEAM_LINE, getCloudinaryRawUrl, isEmpty } from "@swiggy-private/js-utils";
import { captureError } from "@swiggy-private/core-ui";
import { DELIVERY_SPEED_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";

export const getDisplaySlaOptions = (slaOptions: DeliverySpeedDetail[]): DeliverySpeedDetail[] => {
    return slaOptions.filter(
        (option) => option.strategy != null && option.strategy.toUpperCase() !== DELIVERY_SPEED_STRATEGY.NOT_VISIBLE,
    );
};

export const getDeliverySpeedItemPresentationData = (
    item: DeliverySpeedDetail,
    deliverySpeedOptions: DeliverySpeedOption[],
): DeliverySpeedOption | undefined =>
    deliverySpeedOptions.find((option) => option.delivery_speed.toUpperCase() === item.type.toUpperCase());

export const getSlaText = (slaData?: DeliverySpeedDetail): string => {
    if (isEmpty(slaData)) {
        return "";
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { sla_range_min, sla_range_max, sla, o2r, sla_message, type } = slaData;
    const slotSla = sla_message;
    let slaRangeMin = sla_range_min;
    let slaRangeMax = sla_range_max;
    let slaPointvalue = sla;
    if (type === DELIVERY_SPEED.PREORDER) {
        return slotSla || "";
    }
    if (o2r != null) {
        slaRangeMin = o2r.min;
        slaRangeMax = o2r.max;
        slaPointvalue = o2r.point;
    }

    return slaRangeMin != slaRangeMax ? `${slaRangeMin}-${slaRangeMax} ${MINS}` : `${slaPointvalue} ${MINS}`;
};

export const getSlaOptionTitle = (slaOptionData: DeliverySpeedOption, slaData: DeliverySpeedDetail): string => {
    return !isEmpty(slaOptionData.heading_text) ? slaOptionData.heading_text : DELIVERY_SPEED_DISPLAY[slaData.type];
};

export const getDeliverySpeedItemDataTestId = (slaOptionData: DeliverySpeedDetail, isSelected: boolean): string => {
    const deliveryType = slaOptionData?.type?.toUpperCase();
    const selectedSuffix = isSelected ? "_SELECTED" : "_UNSELECTED";

    switch (deliveryType) {
        case DELIVERY_SPEED.BATCH_NO_RUSH:
            return DELIVERY_SPEED_WIDGET_TEST_IDS[
                `DELIVERY_SPEED_NO_RUSH${selectedSuffix}` as keyof typeof DELIVERY_SPEED_WIDGET_TEST_IDS
            ];
        case DELIVERY_SPEED.EXPRESS:
            return DELIVERY_SPEED_WIDGET_TEST_IDS[
                `DELIVERY_SPEED_EXPRESS${selectedSuffix}` as keyof typeof DELIVERY_SPEED_WIDGET_TEST_IDS
            ];
        case DELIVERY_SPEED.STANDARD:
        default:
            return DELIVERY_SPEED_WIDGET_TEST_IDS[
                `DELIVERY_SPEED_STANDARD${selectedSuffix}` as keyof typeof DELIVERY_SPEED_WIDGET_TEST_IDS
            ];
    }
};

export const getDeliverySpeedItemIconDataTestId = (slaOptionData: DeliverySpeedDetail): string => {
    const deliveryType = slaOptionData?.type?.toUpperCase();
    switch (deliveryType) {
        case DELIVERY_SPEED.BATCH_NO_RUSH:
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_ICON_BATCH_NO_RUSH;
        case DELIVERY_SPEED.EXPRESS:
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_ICON_EXPRESS;
        case DELIVERY_SPEED.STANDARD:
        default:
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_ICON_STANDARD;
    }
};

export const fetchLottie = async ({
    lottieId,
    setLottieData,
    setLottieFailed,
}: {
    lottieId?: string;
    setLottieData: React.Dispatch<React.SetStateAction<unknown>>;
    setLottieFailed: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const lottieUrl = getCloudinaryRawUrl(lottieId ?? "", {}, { teamLine: TEAM_LINE.PORTAL }) ?? "";

    try {
        const lottieResponse = await fetch(lottieUrl);
        setLottieData(await lottieResponse.json());
    } catch (error) {
        captureError(error);
        setLottieFailed(true);
    }
};

export const getDeliverySpeedTooltipTestId = (index: number, type?: string) => {
    if (index !== 0) {
        return;
    }

    switch (type) {
        case "TITLE":
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_TOOLTIP_TITLE;
        case "DESCRIPTION":
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_TOOLTIP_DESCRIPTION;
        default:
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_BULLET_POINT;
    }
};

export const isLastOptionWithOneSla = ({
    heading,
    subHeading,
    isDisabled,
    isLastOption,
}: {
    type?: DELIVERY_SPEED;
    heading?: string;
    subHeading?: string;
    isDisabled?: boolean;
    isLastOption?: boolean;
}) => {
    // The case where only one sla option is available
    return isEmpty(heading) && !isEmpty(subHeading) && isDisabled && isLastOption;
};

export const getDeliverySpeedItemSubheadingTestId = ({
    type,
    heading,
    subHeading,
    isDisabled,
    isLastOption,
}: {
    type?: DELIVERY_SPEED;
    heading?: string;
    subHeading?: string;
    isDisabled?: boolean;
    isLastOption?: boolean;
}) => {
    if (isLastOptionWithOneSla({ heading, subHeading, isDisabled, isLastOption })) {
        return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_SUBHEADING_LAST_OPTION;
    }

    switch (type) {
        case DELIVERY_SPEED.STANDARD:
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_SUBHEADING_STANDARD;

        case DELIVERY_SPEED.BATCH_NO_RUSH:
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_SUBHEADING_BATCH_NO_RUSH;

        case DELIVERY_SPEED.EXPRESS:
            return DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_ITEM_SUBHEADING_EXPRESS;
    }
};

export const formatTimeLeft = (seconds: number) => {
    if (seconds <= 0) return "";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const hoursDisplay = hours > 0 ? `${hours}h ` : "";
    const minutesDisplay = minutes > 0 ? `${minutes}m ` : "";
    const secondsDisplay = remainingSeconds > 0 ? `${remainingSeconds}s` : "";

    return `${hoursDisplay}${minutesDisplay}${secondsDisplay}`.trim();
};
